import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Loader2, Users, CalendarDays, TrendingDown, TrendingUp, UserPlus, Repeat,
  Clock, XCircle, CheckCircle2, BarChart3, List,
} from "lucide-react";
import { reportesApi, sucursalesApi } from "../../lib/api";
import { useInicio } from "../../lib/permisos";
import { PageHeader } from "../../components/PageHeader";

const ESTADO_LABELS: Record<string, string> = {
  pendiente: "Pendiente", confirmada: "Confirmada", cancelada: "Cancelada",
  no_show: "No-show", atendida: "Atendida", en_mesa: "En mesa",
};
const ESTADO_COLORS: Record<string, string> = {
  pendiente: "#f59e0b", confirmada: "#10b981", cancelada: "#f43f5e",
  no_show: "#64748b", atendida: "#3b82f6", en_mesa: "#6366f1",
};
const ORIGEN_LABELS: Record<string, string> = {
  web: "Web", web_publica: "Web pública", anfitrion: "Anfitrión",
  telefono: "Teléfono", whatsapp: "WhatsApp", "walk-in": "Walk-in",
};

const ACCENT_PALETTE = ["#3A6B8C", "#9A6A3C", "#5E7C53", "#8C4A52", "#6B5B95", "#A05A2C"];
function accentFor(id: number) { return ACCENT_PALETTE[(id - 1) % ACCENT_PALETTE.length]; }

function fmtFecha(iso: string) {
  return new Date(iso + "T00:00:00").toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "numeric" });
}

type Periodo = "dia" | "semana" | "mes" | "custom";

export default function ReportesPage() {
  const inicio = useInicio();
  const [periodo, setPeriodo] = useState<Periodo>("mes");
  const [sucursal, setSucursal] = useState<number | undefined>(undefined);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const { data: sucursales } = useQuery({
    queryKey: ["sucursales"],
    queryFn: () => sucursalesApi.list(),
  });

  const SUCURSAL_ID: Record<string, number> = Object.fromEntries(
    (sucursales ?? []).map((s) => [s.nombre, s.id]),
  );

  // Query params: presets usan periodo; custom usa desde/hasta
  const queryPeriodo = periodo === "custom" ? "mes" : periodo;
  const queryDesde = periodo === "custom" ? desde : undefined;
  const queryHasta = periodo === "custom" ? hasta : undefined;
  const canQuery = periodo !== "custom" || (!!desde && !!hasta);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["reportes", queryPeriodo, sucursal, queryDesde, queryHasta],
    queryFn: () => reportesApi.reservas(queryPeriodo as "dia" | "semana" | "mes", undefined, sucursal, queryDesde, queryHasta),
    enabled: canQuery,
  });

  // KPIs derivados
  const kpis = useMemo(() => {
    if (!data) return null;
    const total = data.totales.reservas;
    const estadoMap: Record<string, number> = {};
    for (const e of data.porEstado) estadoMap[e.estado] = e.cantidad;
    const confirmadas = (estadoMap["confirmada"] ?? 0) + (estadoMap["atendida"] ?? 0) + (estadoMap["en_mesa"] ?? 0);
    const tasaConfirmacion = total > 0 ? Math.round((confirmadas / total) * 1000) / 10 : 0;
    const totalClientes = data.clientela.nuevos + data.clientela.frecuentes;
    const pctNuevos = totalClientes > 0 ? Math.round((data.clientela.nuevos / totalClientes) * 100) : 0;
    // Hora pico
    const horaPico = data.porHora.length > 0
      ? data.porHora.reduce((a, b) => b.cantidad > a.cantidad ? b : a, data.porHora[0])
      : null;
    return { tasaConfirmacion, pctNuevos, totalClientes, horaPico };
  }, [data]);

  const maxHora = data ? Math.max(1, ...data.porHora.map((h) => h.cantidad)) : 1;

  const PRESETS = [
    { key: "dia" as Periodo, label: "Hoy" },
    { key: "semana" as Periodo, label: "Semana" },
    { key: "mes" as Periodo, label: "Mes" },
    { key: "custom" as Periodo, label: "Rango" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header restaurante */}
      <PageHeader
        crumbs={[{ label: "Inicio", to: inicio }, { label: "Reportes" }]}
        icon={BarChart3}
        actions={[
          { label: "Calendario", icon: CalendarDays, to: "/calendario", variant: "ghost" },
          { label: "Reservas", icon: List, to: "/reservas", variant: "ghost" },
        ]}
      />

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-6">
        {/* Filtros */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Inteligencia de Reservas</h1>
            {data && <p className="text-sm text-slate-500 mt-0.5">{fmtFecha(data.desde)} — {fmtFecha(data.hasta)}</p>}
          </div>
          <div className="flex gap-3 flex-wrap">
            {/* Período */}
            <div className="flex bg-white rounded-lg border border-slate-200 p-1">
              {PRESETS.map((p) => (
                <button key={p.key} onClick={() => setPeriodo(p.key)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${periodo === p.key ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            {/* Sucursal */}
            <div className="flex flex-wrap bg-white rounded-lg border border-slate-200 p-1">
              <button onClick={() => setSucursal(undefined)}
                className={`px-3 py-1.5 text-sm rounded-md font-medium ${!sucursal ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"}`}>Todas</button>
              {(sucursales ?? []).map((s) => (
                <button key={s.id} onClick={() => setSucursal(s.id)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${sucursal === s.id ? "text-white" : "text-slate-600 hover:bg-slate-50"}`}
                  style={sucursal === s.id ? { backgroundColor: accentFor(s.id) } : {}}>
                  {s.nombre}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Rango custom */}
        {periodo === "custom" && (
          <div className="flex items-center gap-3 mb-5 bg-white border border-slate-200 rounded-lg px-4 py-3">
            <label className="text-sm text-slate-600">Desde</label>
            <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
            <label className="text-sm text-slate-600">Hasta</label>
            <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
            {!desde || !hasta ? <span className="text-xs text-amber-600">Seleccioná ambas fechas</span> : null}
          </div>
        )}

        {isLoading ? (
          <div className="p-16 flex items-center justify-center text-slate-400">
            <Loader2 className="size-6 animate-spin mr-2" /> Cargando reportes…
          </div>
        ) : isError ? (
          <div className="p-8 bg-rose-50 border border-rose-200 rounded-lg text-rose-700 text-sm">Error: {(error as Error).message}</div>
        ) : data && kpis ? (
          <>
            {/* ── KPIs principales ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
              <KpiCard icon={<CalendarDays className="size-4" />} label="Reservas" value={data.totales.reservas} />
              <KpiCard icon={<Users className="size-4" />} label="Comensales" value={data.totales.comensales} />
              <KpiCard icon={<Users className="size-4" />} label="Prom. / reserva" value={data.totales.promedioComensales} />
              <KpiCard icon={<CheckCircle2 className="size-4" />} label="Confirmación" value={`${kpis.tasaConfirmacion}%`} accent="emerald" />
              <KpiCard icon={<TrendingDown className="size-4" />} label="No-show" value={`${data.tasas.noShowPct}%`} accent={data.tasas.noShowPct > 10 ? "rose" : "slate"} />
              <KpiCard icon={<XCircle className="size-4" />} label="Cancelación" value={`${data.tasas.cancelacionPct}%`} accent={data.tasas.cancelacionPct > 15 ? "rose" : "slate"} />
              <KpiCard icon={<UserPlus className="size-4" />} label="Clientes nuevos" value={`${kpis.pctNuevos}%`} accent="amber" />
              <KpiCard icon={<Clock className="size-4" />} label="Hora pico" value={kpis.horaPico ? `${kpis.horaPico.hora}:00` : "—"} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Ocupación por hora */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Ocupación por hora</h2>
                {data.porHora.length === 0 ? <Vacio /> : (
                  <div className="flex items-end justify-between gap-1 h-44">
                    {data.porHora.map((h) => {
                      const pct = (h.cantidad / maxHora) * 100;
                      const isPico = kpis.horaPico && h.hora === kpis.horaPico.hora;
                      return (
                        <div key={h.hora} className="flex-1 flex flex-col items-center gap-1 group">
                          <div className="text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                            {h.cantidad} · {h.comensales}p
                          </div>
                          <div
                            className={`w-full rounded-t transition-colors ${isPico ? "bg-slate-800" : "bg-slate-300 group-hover:bg-slate-500"}`}
                            style={{ height: `${Math.max(pct, 3)}%` }}
                            title={`${h.hora}:00 — ${h.cantidad} reservas, ${h.comensales} comensales`}
                          />
                          <div className={`text-[10px] ${isPico ? "text-slate-800 font-bold" : "text-slate-400"}`}>{h.hora}h</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Por estado */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Distribución por estado</h2>
                {data.porEstado.length === 0 ? <Vacio /> : (
                  <div className="space-y-3">
                    {data.porEstado
                      .sort((a, b) => b.cantidad - a.cantidad)
                      .map((e) => {
                        const pct = data.totales.reservas > 0 ? (e.cantidad / data.totales.reservas) * 100 : 0;
                        return (
                          <div key={e.estado}>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-700 font-medium">{ESTADO_LABELS[e.estado] ?? e.estado}</span>
                              <span className="text-slate-500 tabular-nums">{e.cantidad} <span className="text-slate-400">({pct.toFixed(1)}%)</span></span>
                            </div>
                            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: ESTADO_COLORS[e.estado] ?? "#94a3b8" }} />
                            </div>
                          </div>
                        );
                    })}
                  </div>
                )}
              </div>

              {/* Por sucursal */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Comparativa por local</h2>
                {data.porSucursal.length === 0 ? <Vacio /> : (
                  <div className="space-y-2.5">
                    {data.porSucursal
                      .sort((a, b) => Number(b.cantidad) - Number(a.cantidad))
                      .map((s) => {
                        const sucId = SUCURSAL_ID[s.sucursal];
                        const accent = sucId ? accentFor(sucId) : "#94a3b8";
                        const maxSuc = Math.max(1, ...data.porSucursal.map((x) => Number(x.cantidad)));
                        const pct = (Number(s.cantidad) / maxSuc) * 100;
                        return (
                          <Link key={s.sucursal} to={sucId ? `/calendario?sucursal=${sucId}` : "/calendario"}
                            className="block group">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-semibold text-slate-800 group-hover:underline">{s.sucursal}</span>
                              <span className="text-sm text-slate-500 tabular-nums">{s.cantidad} res. · {s.comensales} pers.</span>
                            </div>
                            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: accent }} />
                            </div>
                          </Link>
                        );
                    })}
                  </div>
                )}
              </div>

              {/* Captación: origen + clientela */}
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Captación de clientes</h2>

                {/* Clientela: nuevos vs frecuentes */}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                    <UserPlus className="size-5 text-emerald-600 shrink-0" />
                    <div>
                      <div className="text-2xl font-bold text-slate-800">{data.clientela.nuevos}</div>
                      <div className="text-xs text-slate-500">Nuevos</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                    <Repeat className="size-5 text-blue-600 shrink-0" />
                    <div>
                      <div className="text-2xl font-bold text-slate-800">{data.clientela.frecuentes}</div>
                      <div className="text-xs text-slate-500">Frecuentes</div>
                    </div>
                  </div>
                </div>
                {kpis.totalClientes > 0 && (
                  <div className="mb-5">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Nuevos {kpis.pctNuevos}%</span>
                      <span>Frecuentes {100 - kpis.pctNuevos}%</span>
                    </div>
                    <div className="h-2.5 bg-blue-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${kpis.pctNuevos}%` }} />
                    </div>
                  </div>
                )}

                {/* Canales de origen */}
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Canales de origen</h3>
                <div className="flex flex-wrap gap-2">
                  {data.porOrigen
                    .sort((a, b) => b.cantidad - a.cantidad)
                    .map((o) => (
                    <span key={o.origen} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-sm">
                      <span className="text-slate-700">{ORIGEN_LABELS[o.origen] ?? o.origen}</span>
                      <span className="font-bold text-slate-800">{o.cantidad}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}

function KpiCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: React.ReactNode; accent?: string }) {
  const bg = accent === "emerald" ? "bg-emerald-50 text-emerald-700"
    : accent === "rose" ? "bg-rose-50 text-rose-700"
    : accent === "amber" ? "bg-amber-50 text-amber-700"
    : "bg-slate-50 text-slate-700";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3.5">
      <div className={`size-7 rounded-lg flex items-center justify-center mb-2 ${bg}`}>{icon}</div>
      <div className="text-xl font-bold text-slate-800 leading-tight">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function Vacio() {
  return <div className="py-10 text-center text-slate-400 text-sm">Sin datos en este período</div>;
}
